import React, { createContext, useContext } from 'react';
import { useGameState } from '../hooks/useGameState';

/**
 * GameStateContext
 * 
 * React Context for sharing game state across components.
 * Provides centralized state management for game flow, players, questions, and quest packs.
 */
const GameStateContext = createContext(null);

/**
 * GameStateProvider Component
 * 
 * Context provider that wraps the useGameState hook and makes game state
 * available to all child components through React Context.
 */
export const GameStateProvider = ({ 
  children, 
  authenticatedUser, 
  clientInfo, 
  onUsageUpdate,
  initializeWebRTCConnection,
  conversationMode,
  isAdmin
}) => {
  const gameState = useGameState({
    authenticatedUser,
    clientInfo,
    onUsageUpdate,
    initializeWebRTCConnection,
    conversationMode,
    isAdmin
  });

  return (
    <GameStateContext.Provider value={gameState}>
      {children}
    </GameStateContext.Provider>
  );
};

/**
 * useGameStateContext Hook
 * 
 * Custom hook to access game state from context.
 * Throws an error if used outside of GameStateProvider.
 */
export const useGameStateContext = () => {
  const context = useContext(GameStateContext);
  
  if (!context) {
    throw new Error('useGameStateContext must be used within a GameStateProvider');
  }
  
  return context;
};

export default GameStateProvider;
